import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Book, FileText, Globe, Terminal, HelpCircle, 
  ExternalLink, Rocket, Zap, Shield, MessageCircle, Play
} from 'lucide-react';

const sections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: Rocket,
    content: `
## Welcome to ApexDev

ApexDev is a **free static hosting platform** built for developers. Deploy HTML, CSS, and JavaScript sites in seconds — no configuration, no credit card, no limits.

---

### 1. Create Your Account
Click **Sign Up** from the landing page or go to [/signup](/signup). You'll need:
- A username
- Your email address
- A password

Already have a Megan account? Just **Sign In**.

---

### 2. Create a Project
From the Dashboard, click **New Project** and give it a name. That's it — your project is ready.

Your site will be live at:
\`\`\`
https://your-project-name.meganhost.dpdns.org
\`\`\`

---

### 3. Add Your Files
Open your project and you'll see the file explorer. You can:
- **Create files** directly in the editor
- **Upload files** from your computer
- **Upload a ZIP** of your entire site

Supported file types: HTML, CSS, JavaScript, images, fonts, JSON, XML, and more.

---

### 4. Deploy
Click the **Deploy** button. Your files go live instantly. You'll see a console output showing the deployment progress, and your site URL will be ready to share.

---

### 5. Edit and Redeploy
Made changes? Edit your files, save, and click Deploy again. Your site updates immediately.

---

### Quick Tip: Full Editor
Click **Open Full Editor** from your project for a VS Code-like experience with:
- Syntax highlighting
- File tree navigation
- AI-powered code scanner
- Built-in preview panel
- Keyboard shortcuts (Ctrl+S to save, Ctrl+D to deploy)
`
  },
  {
    id: 'how-to-deploy',
    title: 'How to Deploy',
    icon: Zap,
    content: `
## Deploying Your Site

There are several ways to get your site live on ApexDev.

---

### Method 1: Drag & Drop ZIP
1. Build your site locally (React, Vue, plain HTML — anything static)
2. Zip the output folder (usually \`dist\` or \`build\`)
3. Open your project in ApexDev
4. Click the **Upload ZIP** button
5. Your files are extracted automatically
6. Click **Deploy**

---

### Method 2: Create Files in the Editor
1. Open your project
2. Click **New File**
3. Name it \`index.html\`
4. Write or paste your code
5. Save (Ctrl+S)
6. Create \`style.css\`, \`script.js\`, etc.
7. Click **Deploy**

---

### Method 3: Upload Individual Files
1. Open your project
2. Click the **Upload** icon
3. Select files from your computer
4. Files are added to your project
5. Click **Deploy**

---

### Framework Examples

**React (Vite):**
\`\`\`bash
npm run build
# Upload the dist/ folder as ZIP
\`\`\`

**Vue:**
\`\`\`bash
npm run build
# Upload the dist/ folder as ZIP
\`\`\`

**Plain HTML:**
Just upload your \`.html\`, \`.css\`, and \`.js\` files directly.

---

### After Deploying
Your site is live at your subdomain. Share the URL, add it to your portfolio, or use it as a staging environment.

**Pro tip:** Enable the preview panel (Ctrl+P) in the full editor to see your site update as you work.
`
  },
  {
    id: 'file-structure',
    title: 'File Structure',
    icon: FileText,
    content: `
## How Files Map to URLs

Understanding how your files become URLs helps you organize your site.

---

### Basic Site
\`\`\`
index.html          → https://yoursite.meganhost.dpdns.org/
style.css           → https://yoursite.meganhost.dpdns.org/style.css
script.js           → https://yoursite.meganhost.dpdns.org/script.js
\`\`\`

---

### Multi-Page Site
\`\`\`
index.html          → /
about.html          → /about.html
contact.html        → /contact.html
\`\`\`

---

### Using Folders
\`\`\`
blog/index.html     → /blog
projects/index.html → /projects
assets/logo.png     → /assets/logo.png
css/style.css       → /css/style.css
\`\`\`

---

### Clean URLs
- \`/blog\` automatically serves \`/blog/index.html\`
- \`/about\` serves \`/about/index.html\` (if it exists)

---

### SPA Support
Single Page Applications (React Router, Vue Router) work automatically. Any unknown path falls back to \`index.html\`, so client-side routing works perfectly.

---

### Custom 404 Page
Create a file named \`404.html\` — it will be shown when a page isn't found.

---

### Folder Creation
In the file explorer, create files with forward slashes:
\`\`\`
css/style.css       → creates css/ folder
js/app.js           → creates js/ folder
images/logo.png     → creates images/ folder
\`\`\`
`
  },
  {
    id: 'env-vars',
    title: 'Environment Variables',
    icon: Globe,
    content: `
## Environment Variables

Keep your API keys and configuration secure with environment variables.

---

### Setting Variables
1. Go to your project **Settings**
2. Scroll to **Environment Variables**
3. Add key-value pairs:
   - \`API_KEY\` = \`sk-123456789\`
   - \`SITE_NAME\` = \`My Portfolio\`

---

### Accessing in Code
All environment variables are available as \`window.__ENV__\`:
\`\`\`html
<script>
  console.log(window.__ENV__.API_KEY);    // "sk-123456789"
  console.log(window.__ENV__.SITE_NAME);  // "My Portfolio"
</script>
\`\`\`

---

### Use Cases
- Third-party API keys
- Feature flags
- Environment-specific configuration
- Analytics tracking IDs
- Theme or branding settings

---

### Security Notes
- Env vars are injected at serve time, not stored in your source files
- Values are hidden from the dashboard after saving
- You can update or delete variables anytime
`
  },
  {
    id: 'features',
    title: 'Features & Pricing',
    icon: Shield,
    content: `
## Is ApexDev Really Free?

**Yes. Completely free.** No credit card required. No trial periods. No hidden fees.

---

### What You Get (All Free)
- ✅ Unlimited projects
- ✅ Free subdomain for every project
- ✅ Automatic HTTPS/SSL
- ✅ Built-in code editor with syntax highlighting
- ✅ AI-powered code scanner
- ✅ AI chat assistant
- ✅ Deployment history
- ✅ Analytics dashboard
- ✅ Environment variables
- ✅ Custom 404 pages
- ✅ File upload via ZIP
- ✅ Project export (download as ZIP)

---

### MGC Coins (Play & Earn)
We introduced **MGC Coins** as a fun way to engage with the platform:
- Earn coins by playing games
- Compete on leaderboards
- Unlock badges and achievements

**MGC has no real monetary value** and is not required for any hosting features. It's purely for entertainment and community engagement.

---

### What's Coming
- 🚀 CLI tool for terminal-based deploys
- 🌐 Custom domain support
- 👥 Team collaboration
- 📦 Project templates
- 🤖 More AI features
`
  },
  {
    id: 'support',
    title: 'Support & Community',
    icon: MessageCircle,
    content: `
## Get Help & Connect

We're here to help you succeed with your projects.

---

### Contact Support
- 📧 **Email:** [trackerwanga@gmail.com](mailto:trackerwanga@gmail.com)
- 📱 **WhatsApp:** [+254 769 502 217](https://wa.me/254769502217)
- 📞 **Call:** [+254 758 476 795](tel:+254758476795)

---

### Community
Join our WhatsApp groups to connect with other developers:

- 💬 **WhatsApp Community:** [Join Chat](https://chat.whatsapp.com/Bfr7VjAkxCfHMmpKuIXyt1)
- 📢 **WhatsApp Channel:** [Follow Updates](https://whatsapp.com/channel/0029Vb7FYNA8qIzs2P5dcE37)

---

### Megan Ecosystem
ApexDev is part of the Megan platform. Check out our other services:

| Service | URL | Description |
|---------|-----|-------------|
| Megan APIs | [apis.megan.qzz.io](https://apis.megan.qzz.io) | 585+ endpoints — anime, movies, sports, AI |
| Megan Auth | [auth.megan.qzz.io](https://auth.megan.qzz.io) | Single sign-on for all Megan services |
| Megan AI | [ai.megan.qzz.io](https://ai.megan.qzz.io) | AI assistants and completions |

---

### FAQ

**Q: Can I use a custom domain?**
A: Custom domains are coming soon! For now, every project gets a free subdomain.

**Q: Is there a file size limit?**
A: Files up to 25MB each. Projects can have unlimited files.

**Q: Can I host a React app?**
A: Yes! Build your app locally, zip the output folder, and upload it.

**Q: How long do projects stay online?**
A: Indefinitely. There's no expiration on free projects.

**Q: Can I delete my project?**
A: Yes, from Project Settings > Danger Zone.
`
  }
];

export default function Docs() {
  const [active, setActive] = useState('getting-started');

  const section = sections.find(s => s.id === active) || sections[0];

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <Link to="/dashboard" className="flex items-center gap-2 text-text-muted hover:text-text-primary mb-6">
        <ArrowLeft size={16} /> Back
      </Link>

      <div className="flex items-center gap-3 mb-8">
        <Book size={24} className="text-accent" />
        <div>
          <h1 className="text-xl md:text-2xl font-bold">Documentation</h1>
          <p className="text-text-muted text-sm">Everything you need to deploy and manage your sites</p>
        </div>
      </div>

      <div className="flex gap-6 flex-col md:flex-row">
        {/* Sidebar */}
        <nav className="w-full md:w-52 shrink-0">
          <div className="space-y-1 md:sticky md:top-20">
            {sections.map(s => (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center gap-2.5 transition-colors ${
                  active === s.id ? 'bg-elevated text-accent font-medium' : 'text-text-muted hover:text-text-primary hover:bg-elevated'
                }`}
              >
                <s.icon size={15} className="shrink-0" />
                <span className="truncate">{s.title}</span>
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="card">
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-border">
              <section.icon size={20} className="text-accent" />
              <h2 className="text-lg font-bold">{section.title}</h2>
            </div>
            <div 
              className="docs-content text-sm text-text-muted leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(section.content) }}
            />
          </div>

          {/* Next/Prev navigation */}
          <div className="flex items-center justify-between mt-6 text-sm">
            {sections.findIndex(s => s.id === active) > 0 ? (
              <button onClick={() => {
                const idx = sections.findIndex(s => s.id === active);
                setActive(sections[idx - 1].id);
              }} className="text-accent hover:underline flex items-center gap-1">
                ← {sections[sections.findIndex(s => s.id === active) - 1]?.title}
              </button>
            ) : <div />}
            {sections.findIndex(s => s.id === active) < sections.length - 1 ? (
              <button onClick={() => {
                const idx = sections.findIndex(s => s.id === active);
                setActive(sections[idx + 1].id);
              }} className="text-accent hover:underline flex items-center gap-1">
                {sections[sections.findIndex(s => s.id === active) + 1]?.title} →
              </button>
            ) : <div />}
          </div>
        </div>
      </div>

      <footer className="mt-12 pt-6 border-t border-border flex flex-wrap gap-4 text-xs text-text-muted">
        <a href="https://chat.whatsapp.com/Bfr7VjAkxCfHMmpKuIXyt1" target="_blank" rel="noopener noreferrer" className="hover:text-accent flex items-center gap-1">
          <MessageCircle size={12} /> WhatsApp Community
        </a>
        <a href="https://whatsapp.com/channel/0029Vb7FYNA8qIzs2P5dcE37" target="_blank" rel="noopener noreferrer" className="hover:text-accent flex items-center gap-1">
          <ExternalLink size={12} /> WhatsApp Channel
        </a>
        <a href="mailto:trackerwanga@gmail.com" className="hover:text-accent">trackerwanga@gmail.com</a>
        <span className="ml-auto">ApexDev — Free Static Hosting</span>
      </footer>

      <style>{`
        .docs-content h2 { font-size: 1.1rem; font-weight: 700; color: #FAFAFA; margin-top: 1.5rem; margin-bottom: 0.75rem; }
        .docs-content h3 { font-size: 0.95rem; font-weight: 600; color: #FAFAFA; margin-top: 1.25rem; margin-bottom: 0.5rem; }
        .docs-content p { margin-bottom: 0.75rem; line-height: 1.7; }
        .docs-content strong { color: #FAFAFA; font-weight: 600; }
        .docs-content code { background: #111; padding: 2px 6px; border-radius: 4px; font-size: 0.8rem; color: #22C55E; font-family: 'Geist Mono', monospace; }
        .docs-content pre { background: #0A0A0A; border: 1px solid #1A1A1A; border-radius: 8px; padding: 1rem; overflow-x: auto; margin: 1rem 0; }
        .docs-content pre code { background: none; padding: 0; color: #FAFAFA; }
        .docs-content ul { list-style: disc; margin-left: 1.25rem; margin-bottom: 0.75rem; }
        .docs-content li { margin-bottom: 0.25rem; line-height: 1.6; }
        .docs-content a { color: #22C55E; text-decoration: underline; }
        .docs-content a:hover { color: #16A34A; }
        .docs-content hr { border: none; border-top: 1px solid #1A1A1A; margin: 1.5rem 0; }
        .docs-content table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.85rem; }
        .docs-content th { text-align: left; padding: 8px 12px; border-bottom: 1px solid #2A2A2A; color: #FAFAFA; font-weight: 600; }
        .docs-content td { padding: 8px 12px; border-bottom: 1px solid #1A1A1A; }
      `}</style>
    </div>
  );
}

function renderMarkdown(text: string): string {
  return text
    .replace(/### (.+)/g, '<h3>$1</h3>')
    .replace(/## (.+)/g, '<h2>$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) =>
      `<pre><code>${code.trim()}</code></pre>`
    )
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/---/g, '<hr>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\| (.+) \|/g, (match) => {
      if (match.includes('---')) return '';
      const cells = match.split('|').filter(c => c.trim());
      const isHeader = cells.some(c => c.includes('**'));
      return isHeader ? '' : match;
    });
}

